"use client";

import { Box, Container, Title, Text, Button, Stack, Paper, Radio, Group, Progress, Flex, Card } from "@mantine/core";
import { IconArrowRight, IconArrowLeft } from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { trackMetaPixelEvent } from "@/lib/metaPixelAnalytics";

type StepType = "question" | "interstitial";

interface QuizQuestion {
  type: "question";
  id: string;
  question: string;
  options: {
    value: string;
    label: string;
  }[];
}

interface Interstitial {
  type: "interstitial";
  id: string;
  title: string;
  body: string;
}

type QuizStep = QuizQuestion | Interstitial;

const quizSteps: QuizStep[] = [
  // Q1 – Anchor the Problem
  {
    type: "question",
    id: "color_challenge",
    question: "When choosing clothes, what feels hardest?",
    options: [
      { value: "knowing_colors", label: "Knowing which colors actually suit me" },
      { value: "avoiding_bad", label: "Avoiding colors that make me look tired or washed out" },
      { value: "understanding_why", label: "Understanding why some colors work and others don't" },
      { value: "guessing", label: "I mostly guess and hope for the best" },
    ],
  },
  // Interstitial #1 – Conceptual Value
  {
    type: "interstitial",
    id: "interstitial_biology",
    title: "Color isn't subjective — it's biological",
    body: "Skin undertone, contrast, and depth determine which colors support your features.\n\nThat's why a color that looks amazing on one person can look off on another.",
  },
  // Q2 – Contrast Perception
  {
    type: "question",
    id: "contrast_perception",
    question: "Which statement feels most true?",
    options: [
      { value: "bold_overpower", label: "Bold colors often overpower me" },
      { value: "soft_disappear", label: "Soft colors sometimes make me disappear" },
      { value: "high_contrast_best", label: "High-contrast outfits usually look best on me" },
      { value: "cant_tell", label: "I honestly can't tell" },
    ],
  },
  // Q3 – Feedback Memory
  {
    type: "question",
    id: "compliment_pattern",
    question: "When people compliment your appearance, what's usually mentioned?",
    options: [
      { value: "specific_color", label: "A specific color I'm wearing" },
      { value: "skin_healthy", label: "My skin looks clear / healthy" },
      { value: "eyes_standout", label: "My eyes stand out" },
      { value: "no_color_comments", label: "I don't get color-specific comments" },
    ],
  },
  // Interstitial #2 – Authority + Reassurance
  {
    type: "interstitial",
    id: "interstitial_reassurance",
    title: "Most people never learn this",
    body: "Over 70% of people wear colors that fight their natural features — not because they're bad at style, but because color analysis isn't taught.",
  },
  // Q4 – Sensitivity Test
  {
    type: "question",
    id: "wrong_color_effect",
    question: "What happens when you wear the wrong color?",
    options: [
      { value: "skin_dull", label: "My skin looks dull or gray" },
      { value: "look_tired", label: "I look tired even when I'm not" },
      { value: "features_undefined", label: "My features seem less defined" },
      { value: "feel_off", label: "I'm not sure — I just feel \"off\"" },
    ],
  },
  // Q5 – Commitment Question
  {
    type: "question",
    id: "timing",
    question: "How soon do you want clarity on your colors?",
    options: [
      { value: "immediately", label: "Immediately" },
      { value: "this_week", label: "This week" },
      { value: "sometime_soon", label: "Sometime soon" },
      { value: "just_curious", label: "Just curious right now" },
    ],
  },
];

export default function Content() {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const totalSteps = quizSteps.length;
  const questionSteps = quizSteps.filter((s) => s.type === "question");
  const currentStepData = quizSteps[currentStep];
  const isQuestion = currentStepData.type === "question";
  const isInterstitial = currentStepData.type === "interstitial";

  // Count which question number we're on (for display)
  const questionNumber = quizSteps.slice(0, currentStep + 1).filter((s) => s.type === "question").length;
  const totalQuestions = questionSteps.length;

  useEffect(() => {
    trackMetaPixelEvent("StyleAssessmentStarted");
  }, []);

  const handleAnswer = (questionId: string, value: string) => {
    if (isTransitioning) return;

    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    setIsTransitioning(true);

    setTimeout(() => {
      advanceStep();
      setIsTransitioning(false);
    }, 400);
  };

  const advanceStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((prev) => prev + 1);
      trackMetaPixelEvent("StyleAssessmentQuestionAnswered", { step: currentStep + 1 });
    } else {
      handleSubmit();
    }
  };

  const handleContinue = () => {
    advanceStep();
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    } else {
      router.push("/ai-color-analysis");
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    trackMetaPixelEvent("StyleAssessmentCompleted");
    localStorage.setItem("styleAssessmentAnswers", JSON.stringify(answers));
    router.push("/ai-color-analysis/results-preview");
  };

  const progress = ((currentStep + 1) / totalSteps) * 100;
  const currentAnswer = isQuestion ? answers[(currentStepData as QuizQuestion).id] : null;

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Stack align="center" ta="center">
          <Title order={1} fz={{ base: "xl", md: "xxl" }}>
            Color Analysis
          </Title>
          {isQuestion && (
            <Text c="dimmed" fz="lg">
              Question {questionNumber} of {totalQuestions}
            </Text>
          )}
          <Progress value={progress} size="lg" radius="xl" w="100%" maw={400} color="teal" />
        </Stack>

        {/* Question Card */}
        {isQuestion && (
          <Card
            withBorder
            p="xl"
            radius="md"
            maw={700}
            mx="auto"
            w="100%"
            style={{
              opacity: isTransitioning ? 0.8 : 1,
              transform: isTransitioning ? "translateY(10px)" : "translateY(0)",
              transition: "all 0.3s ease",
            }}
          >
            <Stack gap="lg">
              <Title order={2} fz="xl" ta="center" c="dark.8">
                {(currentStepData as QuizQuestion).question}
              </Title>

              <Stack gap="sm">
                {(currentStepData as QuizQuestion).options.map((option) => (
                  <Paper
                    key={option.value}
                    p="md"
                    withBorder
                    radius="md"
                    style={{
                      cursor: isTransitioning ? "not-allowed" : "pointer",
                      borderColor: currentAnswer === option.value ? "var(--mantine-color-teal-4)" : undefined,
                      backgroundColor: currentAnswer === option.value ? "var(--mantine-color-teal-0)" : undefined,
                      opacity: isTransitioning ? 0.7 : 1,
                      transition: "all 0.2s ease",
                      transform: currentAnswer === option.value ? "scale(1.02)" : "scale(1)",
                    }}
                    onMouseEnter={(e) => {
                      if (!isTransitioning && currentAnswer !== option.value) {
                        e.currentTarget.style.backgroundColor = "var(--mantine-color-gray-0)";
                        e.currentTarget.style.borderColor = "var(--mantine-color-gray-3)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isTransitioning && currentAnswer !== option.value) {
                        e.currentTarget.style.backgroundColor = "";
                        e.currentTarget.style.borderColor = "";
                      }
                    }}
                    onClick={() => handleAnswer((currentStepData as QuizQuestion).id, option.value)}
                  >
                    <Group gap="md" align="center">
                      <Radio
                        checked={currentAnswer === option.value}
                        onChange={() => {}}
                        color="teal"
                        style={{ pointerEvents: "none" }}
                      />
                      <Text fw={500}>{option.label}</Text>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </Stack>
          </Card>
        )}

        {/* Interstitial Card */}
        {isInterstitial && (
          <Card
            withBorder
            p="xl"
            radius="md"
            maw={600}
            mx="auto"
            w="100%"
            bg="gray.0"
          >
            <Stack gap="lg" align="center" ta="center">
              <Title order={2} fz={{ base: "lg", md: "xl" }} c="dark.8">
                {(currentStepData as Interstitial).title}
              </Title>
              <Text fz="md" c="dark.6" style={{ whiteSpace: "pre-line", lineHeight: 1.7 }}>
                {(currentStepData as Interstitial).body}
              </Text>
              <Button
                size="lg"
                color="teal"
                rightSection={<IconArrowRight size={18} />}
                onClick={handleContinue}
                mt="md"
              >
                Continue
              </Button>
            </Stack>
          </Card>
        )}

        {/* Navigation - only show for questions */}
        {isQuestion && (
          <Flex justify="space-between" align="center" maw={700} mx="auto" w="100%">
            <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={handleBack} color="gray">
              {currentStep === 0 ? "Back to Home" : "Previous"}
            </Button>

            <Button
              rightSection={<IconArrowRight size={16} />}
              onClick={advanceStep}
              disabled={!currentAnswer || isTransitioning}
              loading={isSubmitting || isTransitioning}
              color="teal"
              size="md"
            >
              {isTransitioning
                ? "Loading..."
                : currentStep === totalSteps - 1
                ? "See My Results"
                : "Next"}
            </Button>
          </Flex>
        )}

        {/* Back button for interstitials */}
        {isInterstitial && (
          <Flex justify="center" maw={600} mx="auto" w="100%">
            <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={handleBack} color="gray">
              Previous
            </Button>
          </Flex>
        )}
      </Stack>
    </Container>
  );
}
